import {
  Box,
  Button,
  Modal,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setBuildingState } from "../../store/actions/permitFormActions/buildingStateActions";
import {
  bondingLabel,
  feeSimpleLabel,
  mortgageLabel,
  termiteLabel,
  projectLabel,
} from "../../data/buildingPermit/fieldDatas";
import { formikRef } from "../../store/formikStore/formikStore";
import { LoadingButton } from "@mui/lab";
import {
  handleSaveProjects,
  pushNewDynamicSavedData,
} from "../../store/actions/dataActions";

const FinancialBPModal = ({
  type,
  open,
  title,
  handleStateNo,
  fieldsToCheck,
}) => {
  const formik = formikRef.current;
  const dispatch = useDispatch();

  const {
    newProjectLoading,
    feeSimpleLoading,
    bondingLoading,
    mortgageLoading,
    termiteLoading,
  } = useSelector((state) => state.buildingState);

  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [fieldValues, setFieldValues] = useState({
    project: {
      project_contact: "",
      project_c_phone: "",
      loading: newProjectLoading,
      formikName: "projectContact",
      savedProjectName: "projectContact",
      label: projectLabel,
    },
    feeSimple: {
      fee_simple_name: "",
      fee_simple_address: "",
      loading: feeSimpleLoading,
      formikName: "feeSimpleTitleholder",
      savedProjectName: "feeSimple",
      label: feeSimpleLabel,
    },
    bonding: {
      bonding_name: "",
      bonding_address: "",
      loading: bondingLoading,
      formikName: "bondingCompany",
      savedProjectName: "bonding",
      label: bondingLabel,
    },
    mortgage: {
      mortgage_name: "",
      mortgage_address: "",
      loading: mortgageLoading,
      formikName: "mortgageLender",
      savedProjectName: "mortgageLender",
      label: mortgageLabel,
    },
    termite: {
      protection_by: "",
      treatment_method: "",
      loading: termiteLoading,
      formikName: "termite",
      savedProjectName: "termite",
      label: termiteLabel,
    },
  });

  useEffect(() => {
    const callFunc = () => {
      let error = false;

      fieldsToCheck.forEach((item) => {
        const val = fieldValues[type][item];

        if (item === "project_c_phone") {
          if (val === "" || val.length < 12) {
            error = true;
          }
        } else if (item === "treatment_method") {
          // treatment_method is a select field, just check if it's empty
          if (val === "") {
            error = true;
          }
        } else {
          if (val === "" || val.length < 3) {
            error = true;
          }
        }
      });

      setSubmitDisabled(error);
    };

    callFunc();
  }, [fieldValues, fieldsToCheck, type]);

  const runFieldCange = (event) => {
    const { name, value } = event.target;

    setFieldValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [name]: value,
      },
    }));
  };

  const handleAddNew = async () => {
    try {
      await dispatch(setBuildingState({ [fieldValues[type].loading]: true }));

      const newID = Date.now().toString();
      const createdAt = Math.floor(Date.now() / 1000);
      const reduxKey = fieldValues[type].savedProjectName;

      const savedProjectObj = {
        id: newID,
        [fieldsToCheck[0]]: fieldValues[type][fieldsToCheck[0]],
        [fieldsToCheck[1]]: fieldValues[type][fieldsToCheck[1]],
        created_at: createdAt,
      };

      await dispatch(
        pushNewDynamicSavedData({ key: reduxKey, val: savedProjectObj })
      );

      await dispatch(handleSaveProjects());
      formik?.setValues((prevValues) => ({
        ...prevValues,
        [fieldValues[type].formikName]: newID,
        [fieldsToCheck[0]]: fieldValues[type][fieldsToCheck[0]],
        [fieldsToCheck[1]]: fieldValues[type][fieldsToCheck[1]],
      }));

      handleStateNo();
    } catch (e) {
    } finally {
      await dispatch(setBuildingState({ [fieldValues[type].loading]: false }));
    }
  };

  const cleanPhone = (e) => {
    const { name, value } = e.target;

    let digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters

    // If the number of digits is greater than 10, slice to 10 digits
    if (digitsOnly?.length > 10) {
      digitsOnly = digitsOnly.slice(0, 10);
    }

    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    setFieldValues((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [name]: formattedPhoneNumber,
      },
    }));
  };

  return (
    <Modal
      open={open}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      sx={{
        "& .MuiBackdrop-root": {
          backgroundColor: "transparent",
        },
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "90%", // Adjust width for smaller screens
          maxWidth: 600, // Set a maximum width
          bgcolor: "background.paper",
          boxShadow: "1px 10px 10px 5px rgba(0, 0, 0, 0.5)",
          borderRadius: "8px",
        }}
      >
        {/* Title Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <Typography variant="h5" align="center" color="primary">
            {title}
          </Typography>
        </Box>

        {/* Scrollable Content Section */}
        <Box
          sx={{
            maxHeight: "60vh", // Limit height to 60% of the viewport
            overflowY: "auto", // Enable scrolling when content overflows
            p: 2,
          }}
        >
          {/* {mapCheck && selectedPro && ( */}
          <>
            {fieldsToCheck?.map((field, i) => {
              if (field === "treatment_method") {
                return (
                  <FormControl
                    key={field + i}
                    fullWidth
                    margin="normal"
                    variant="filled"
                  >
                    <InputLabel>{fieldValues[type].label[field]}</InputLabel>
                    <Select
                      name={field}
                      value={fieldValues[type][field]}
                      onChange={runFieldCange}
                      onBlur={(event) => {
                        formik?.handleBlur(event);
                        formik?.setFieldTouched(field, true, true);
                      }}
                      error={
                        formik?.touched[field] && Boolean(formik?.errors[field])
                      }
                      MenuProps={{
                        PaperProps: {
                          style: {
                            marginTop: "1.5px",
                            maxHeight: 200,
                            width: "auto",
                            maxWidth: "100%",
                          },
                        },
                      }}
                    >
                      <MenuItem value="treatment_soil">Soil</MenuItem>
                      <MenuItem value="treatment_bait">Bait</MenuItem>
                      <MenuItem value="treatment_wood">Wood</MenuItem>
                    </Select>
                  </FormControl>
                );
              }
              return (
                <TextField
                  autoComplete="off"
                  key={field + i}
                  fullWidth
                  onKeyUp={(e) => {
                    if (field === "project_c_phone") {
                      cleanPhone(e);
                    }
                  }}
                  margin="normal"
                  variant="filled"
                  label={fieldValues[type].label[field]}
                  name={field}
                  value={fieldValues[type][field]}
                  onChange={runFieldCange}
                  onBlur={(event) => {
                    formik?.handleBlur(event);
                    formik?.setFieldTouched(field, true, true);
                  }}
                  error={formik?.touched[field] && Boolean(formik?.errors[field])}
                  helperText={formik?.touched[field] && formik?.errors[field]}
                />
              );
            })}
          </>
        </Box>

        {/* Footer Section with Buttons */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            p: 2,
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Button
            fullWidth
            onClick={() => {
              handleStateNo();
            }}
            color="error"
            variant="contained"
          >
            Cancel
          </Button>
          <LoadingButton
            loading={fieldValues[type].loading}
            disabled={submitDisabled}
            fullWidth
            onClick={handleAddNew}
            color="success"
            variant="contained"
          >
            Add
          </LoadingButton>
        </Box>
      </Box>
    </Modal>
  );
};

export default React.memo(FinancialBPModal);
